'use client';

import { useEffect } from 'react';
import { getOptanonCategoryClass, ONETRUST_CATEGORIES, onConsentChange } from 'libs/oneTrust';
import { hasConsentFromBrowser } from 'libs/oneTrust/clientConsent';
import { isMobileDevice } from 'utils/device';
import { isLocalEnvironment } from 'utils/environment';
import { getOneLinkAnonIdParams } from 'utils/linkHelpers';

const APPSFLYER_SDK_SRC = 'https://websdk.appsflyersdk.com?st=banners';
export const APPSFLYER_SDK_SCRIPT_ID = 'appsflyer-smart-banner-sdk';
// Matches the `z-50` header layer the banner slot renders in.
const BANNER_Z_INDEX = 50;

interface AppsFlyerShowBannerOptions {
  bannerContainerQuery?: string;
  bannerZIndex: number;
  additionalParams: Record<string, string>;
  creativeId?: string;
}

interface AppsFlyerSdkId {
  banners: {
    key: string;
  };
}

interface AppsFlyerSdk {
  (plugin: 'banners', method: 'showBanner', options: AppsFlyerShowBannerOptions): void;
  (plugin: 'banners', method: 'hideBanner'): void;
  q?: unknown[][];
  id?: AppsFlyerSdkId;
  plugins?: Record<string, unknown>;
}

declare global {
  interface Window {
    AF?: AppsFlyerSdk;
    AppsFlyerSdkObject?: string;
  }
}

interface SmartBannerClientProps {
  containerId: string;
  stickyToTop: boolean;
  bannerId?: string;
  adset: string;
  deviceId: string;
  nonce: string;
}

/**
 * AppsFlyer's standard loader with its trailing auto-show dropped: queue calls on a `window.AF` stub
 * until the SDK script replaces it, so the banner shows only where this module renders.
 */
function loadAppsFlyerSdk(webKey: string, nonce: string): AppsFlyerSdk {
  window.AppsFlyerSdkObject = 'AF';

  if (!window.AF) {
    const queue: unknown[][] = [];
    window.AF = Object.assign((...args: unknown[]) => queue.push([Date.now(), ...args]), {
      q: queue,
    });
  }

  window.AF.id ??= { banners: { key: webKey } };
  window.AF.plugins ??= {};

  if (!document.getElementById(APPSFLYER_SDK_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = APPSFLYER_SDK_SCRIPT_ID;
    script.src = APPSFLYER_SDK_SRC;
    script.async = true;
    script.nonce = nonce;
    script.className = getOptanonCategoryClass(ONETRUST_CATEGORIES.TARGETING);
    document.head.appendChild(script);
  }

  return window.AF;
}

/**
 * Phones only, decided in the browser because the page HTML is cached across devices.
 * Until OneTrust first dispatches, the cookie/GPC check decides, so cookieless new visitors still get the banner.
 * The SDK can't be unloaded, so a Targeting (C0004) revoke only hides the banner.
 */
export const SmartBannerClient = ({
  containerId,
  stickyToTop,
  bannerId,
  adset,
  deviceId,
  nonce,
}: SmartBannerClientProps) => {
  useEffect(() => {
    const webKey = process.env.NEXT_PUBLIC_APPSFLYER_WEB_KEY;
    if (!webKey) {
      if (isLocalEnvironment()) {
        console.warn('SmartBanner: NEXT_PUBLIC_APPSFLYER_WEB_KEY is not set, so the AppsFlyer banner is disabled.');
      }
      return;
    }
    if (!isMobileDevice({ userAgent: navigator.userAgent })) return;

    let isShown = false;
    let hasDispatched = false;

    // OneTrust re-dispatches unchanged consent and replays it on subscribe, so only act on a change.
    const setBannerShown = (shouldShow: boolean) => {
      if (shouldShow === isShown) return;
      isShown = shouldShow;
      if (shouldShow) {
        loadAppsFlyerSdk(webKey, nonce)('banners', 'showBanner', {
          // Any container (even 'body') gets `transform: translate(0)` for bottom and splash banners, which
          // pins their `position: fixed` to it, so only sticky-to-top banners mount in our header slot.
          ...(stickyToTop && { bannerContainerQuery: `#${containerId}` }),
          bannerZIndex: BANNER_Z_INDEX,
          additionalParams: { af_adset: adset, ...getOneLinkAnonIdParams(deviceId) },
          // Without an id AppsFlyer picks from every active banner on the web key.
          ...(bannerId && { creativeId: bannerId }),
        });
      } else {
        window.AF?.('banners', 'hideBanner');
      }
    };

    const unsubscribe = onConsentChange(activeGroups => {
      hasDispatched = true;
      setBannerShown(activeGroups.includes(ONETRUST_CATEGORIES.TARGETING));
    });

    if (!hasDispatched) {
      setBannerShown(hasConsentFromBrowser(ONETRUST_CATEGORIES.TARGETING));
    }

    return () => {
      unsubscribe();
      setBannerShown(false);
    };
  }, [containerId, stickyToTop, bannerId, adset, deviceId, nonce]);

  return <div id={containerId} data-testid="smart-banner" />;
};
